/**
 * The envelope: a self-contained encrypted document plus the wrapped keys that
 * open it.
 *
 * Everything here runs in the browser and needs NO server. An envelope is a
 * single file the owner keeps wherever they like — their own storage, a USB
 * stick, a git repo, a lawyer's safe.
 *
 * Two things are deliberately absent from the file:
 *
 *  - **The recipient key strings.** Storing them beside the keys they unwrap
 *    would make the envelope unlock itself. Only a verifier hash is kept.
 *  - **Everything identifying.** From v2, the filename, MIME type, notes and
 *    the recipients' names and emails are encrypted under the document key.
 *    v1 left them in the clear, so an envelope in any public location announced
 *    that someone has a will and who inherits — the document stayed safe while
 *    everything around it leaked.
 *
 * What a v2 envelope still reveals to someone holding the file: that it is an
 * AliveGuardian envelope, roughly how large the document is, and how many
 * recipients there are. Nothing else.
 */
import {
  deriveKeyFromPassphrase,
  deriveKeyFromRecipientString,
  generateRecipientKeyString,
  hashRecipientKeyString,
  wrapDEKWithDerivedKey,
  unwrapDEKWithDerivedKey,
  arrayBufferToBase64,
  base64ToArrayBuffer,
  AES_ALGORITHM,
  AES_KEY_LENGTH,
  AES_IV_LENGTH,
  AES_KEY_USAGES,
} from '@agl/shared/crypto';

export const ENVELOPE_VERSION = 2;

/** An opaque encrypted blob: IV plus ciphertext, both base64. */
interface Sealed {
  iv: string;
  ciphertext: string;
}

/** Encrypted under the document key. Readable by anyone who can open the file. */
export interface EnvelopeMeta {
  name: string;
  mimeType: string;
  size: number;
  notes?: string;
  createdAt: number;
  recipients: { name: string; email: string }[];
}

/** What survives in the clear. Every field is either opaque or a hash. */
export interface EnvelopeRecipientV2 {
  /** Verifier only. Proves a key string matches; reveals nothing about it. */
  keyHash: string;
  /** The DEK wrapped to this recipient's key string. */
  wrappedDek: string;
}

export interface EnvelopeV2 {
  version: 2;
  meta: Sealed;
  document: Sealed;
  owner: { salt: string; wrappedDek: string };
  recipients: EnvelopeRecipientV2[];
}

/** The original format. Still readable; no longer written. */
export interface EnvelopeV1 {
  version: 1;
  createdAt: number;
  document: { name: string; mimeType: string; size: number; iv: string; ciphertext: string };
  owner: { salt: string; wrappedDek: string };
  recipients: { name: string; email: string; keyHash: string; wrappedDek: string }[];
}

export type Envelope = EnvelopeV1 | EnvelopeV2;

export interface IssuedKey {
  name: string;
  email: string;
  /** Shown ONCE. Never persisted, never placed in the envelope. */
  keyString: string;
}

export interface ContactInput {
  name: string;
  email: string;
}

/** Opened contents: the file plus whatever the envelope knew about it. */
export interface OpenedEnvelope {
  blob: Blob;
  meta: EnvelopeMeta;
}

function randomIv(): Uint8Array<ArrayBuffer> {
  const iv = new Uint8Array(new ArrayBuffer(AES_IV_LENGTH));
  crypto.getRandomValues(iv);
  return iv;
}

async function seal(dek: CryptoKey, plaintext: BufferSource): Promise<Sealed> {
  const iv = randomIv();
  const ciphertext = await crypto.subtle.encrypt({ name: AES_ALGORITHM, iv }, dek, plaintext);
  return { iv: arrayBufferToBase64(iv.buffer), ciphertext: arrayBufferToBase64(ciphertext) };
}

async function unseal(dek: CryptoKey, sealed: Sealed): Promise<ArrayBuffer> {
  const iv = new Uint8Array(base64ToArrayBuffer(sealed.iv));
  return crypto.subtle.decrypt(
    { name: AES_ALGORITHM, iv },
    dek,
    base64ToArrayBuffer(sealed.ciphertext)
  );
}

/**
 * Encrypt a document and wrap its DEK for the owner and every recipient.
 *
 * One DEK per document, used for both the file and its metadata, so anyone who
 * can open the document can read its name — and nobody else can. The owner's
 * copy of the DEK is wrapped to their passphrase, each recipient's to their own
 * key string, so nobody's access depends on anyone else's.
 */
export async function sealEnvelope(
  file: File,
  passphrase: string,
  contacts: ContactInput[],
  notes?: string
): Promise<{ envelope: EnvelopeV2; issuedKeys: IssuedKey[] }> {
  const dek = await crypto.subtle.generateKey(
    { name: AES_ALGORITHM, length: AES_KEY_LENGTH },
    true,
    AES_KEY_USAGES
  );

  const document = await seal(dek, await file.arrayBuffer());

  const { key: ownerKey, salt } = await deriveKeyFromPassphrase(passphrase);
  const ownerWrappedDek = await wrapDEKWithDerivedKey(dek, ownerKey);

  const recipients: EnvelopeRecipientV2[] = [];
  const issuedKeys: IssuedKey[] = [];

  for (const contact of contacts) {
    const keyString = generateRecipientKeyString();
    const recipientKey = await deriveKeyFromRecipientString(keyString);
    recipients.push({
      keyHash: await hashRecipientKeyString(keyString),
      wrappedDek: await wrapDEKWithDerivedKey(dek, recipientKey),
    });
    issuedKeys.push({ name: contact.name, email: contact.email, keyString });
  }

  const meta: EnvelopeMeta = {
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    createdAt: Date.now(),
    recipients: contacts.map((c) => ({ name: c.name, email: c.email })),
  };
  if (notes) meta.notes = notes;

  const sealedMeta = await seal(dek, new TextEncoder().encode(JSON.stringify(meta)));

  return {
    envelope: {
      version: 2,
      meta: sealedMeta,
      document,
      owner: { salt, wrappedDek: ownerWrappedDek },
      recipients,
    },
    issuedKeys,
  };
}

/** Open as the owner, with the passphrase. */
export async function openAsOwner(env: Envelope, passphrase: string): Promise<OpenedEnvelope> {
  const { key } = await deriveKeyFromPassphrase(passphrase, env.owner.salt);
  return openWith(env, env.owner.wrappedDek, key, 'passphrase');
}

/** Open as a recipient, with a key string. */
export async function openAsRecipient(env: Envelope, keyString: string): Promise<OpenedEnvelope> {
  const normalised = keyString.trim().toUpperCase();
  const hash = await hashRecipientKeyString(normalised);
  const match = env.recipients.find((r) => r.keyHash === hash);
  if (!match) throw new Error('That key does not open this envelope.');
  const key = await deriveKeyFromRecipientString(normalised);
  return openWith(env, match.wrappedDek, key, 'key');
}

async function openWith(
  env: Envelope,
  wrappedDek: string,
  unwrappingKey: CryptoKey,
  kind: 'passphrase' | 'key'
): Promise<OpenedEnvelope> {
  let dek: CryptoKey;
  try {
    dek = await unwrapDEKWithDerivedKey(wrappedDek, unwrappingKey);
  } catch (cause) {
    throw new Error(
      kind === 'passphrase' ? 'Wrong passphrase.' : 'That key does not open this envelope.',
      { cause }
    );
  }

  try {
    if (env.version === 2) {
      const meta = JSON.parse(
        new TextDecoder().decode(await unseal(dek, env.meta))
      ) as EnvelopeMeta;
      const plain = await unseal(dek, env.document);
      return { blob: new Blob([plain], { type: meta.mimeType }), meta };
    }

    const plain = await unseal(dek, { iv: env.document.iv, ciphertext: env.document.ciphertext });
    return {
      blob: new Blob([plain], { type: env.document.mimeType }),
      meta: {
        name: env.document.name,
        mimeType: env.document.mimeType,
        size: env.document.size,
        createdAt: env.createdAt,
        recipients: env.recipients.map((r) => ({ name: r.name, email: r.email })),
      },
    };
  } catch (cause) {
    // The key unwrapped, so the credential was right — this is damage, not user
    // error. Reporting "wrong passphrase" here would send someone chasing a
    // password that was correct all along.
    throw new Error('The document is damaged and could not be decrypted.', { cause });
  }
}

export function parseEnvelope(text: string): Envelope {
  const parsed = JSON.parse(text) as Envelope;
  if (parsed?.version !== 1 && parsed?.version !== 2) {
    throw new Error('Not a recognised AliveGuardian envelope.');
  }
  if (!parsed.owner?.wrappedDek || !Array.isArray(parsed.recipients)) {
    throw new Error('This envelope is incomplete.');
  }
  const hasBody =
    parsed.version === 2
      ? !!parsed.meta?.ciphertext && !!parsed.document?.ciphertext
      : !!parsed.document?.ciphertext;
  if (!hasBody) throw new Error('This envelope is incomplete.');
  return parsed;
}

/** What can be shown about an envelope BEFORE it is opened. */
export function describeEnvelope(env: Envelope): {
  name: string | null;
  createdAt: number | null;
  recipientCount: number;
} {
  return env.version === 2
    ? { name: null, createdAt: null, recipientCount: env.recipients.length }
    : { name: env.document.name, createdAt: env.createdAt, recipientCount: env.recipients.length };
}
